/**
 * Utility functions for EmailComposer component
 */

export interface Emoji {
  emoji: string;
  name: string;
}

/**
 * Get HTML content from RichTextEditor component
 */
export function getEditorHTML(editor: any, fallback: string = ""): string {
  if (!editor) return fallback;

  try {
    // Method 1: Direct access via getHTML
    if (typeof editor.getHTML === "function") {
      return editor.getHTML() || fallback;
    }

    // Method 2: Access via $refs.quillEditor
    if (editor.$refs && editor.$refs.quillEditor) {
      const quill = editor.$refs.quillEditor.getQuill();
      if (quill && quill.root) {
        return quill.root.innerHTML || fallback;
      }
    }

    // Method 3: Access quillInstance property
    if (editor.quillInstance && editor.quillInstance.root) {
      return editor.quillInstance.root.innerHTML || fallback;
    }
  } catch (error) {
    // Silently fail and return fallback
  }

  return fallback;
}

/**
 * Get Quill instance from RichTextEditor component
 */
export function getQuillInstance(editor: any): any {
  if (!editor) return null;

  try {
    // Method 1: Direct access via getQuill
    if (typeof editor.getQuill === "function") {
      return editor.getQuill();
    }

    // Method 2: Access via $refs
    if (editor.$refs && editor.$refs.quillEditor) {
      return editor.$refs.quillEditor.getQuill();
    }

    // Method 3: Access quillInstance property
    if (editor.quillInstance) {
      return editor.quillInstance;
    }
  } catch (error) {
    // Silently fail
  }

  return null;
}

/**
 * Check if body content is empty
 */
export function isBodyEmpty(bodyContent: string): boolean {
  if (!bodyContent || bodyContent.trim().length === 0) {
    return true;
  }

  const textContent = bodyContent.replace(/<[^>]*>/g, "").trim();

  return (
    !textContent ||
    textContent.length === 0 ||
    bodyContent.match(/^<p[^>]*><br\s*\/?><\/p>$/i) !== null ||
    bodyContent.match(/^<p[^>]*><\/p>$/i) !== null ||
    bodyContent === "<p><br></p>" ||
    bodyContent === "<p></p>" ||
    bodyContent.trim() === ""
  );
}

/**
 * Check if signature is present in body content
 */
export function hasSignatureInBody(
  bodyContent: string,
  signature: { content: string } | null
): boolean {
  if (!signature || !signature.content) {
    return false;
  }

  const signatureText = signature.content.replace(/<[^>]*>/g, "").trim();
  if (signatureText.length === 0) {
    return false;
  }

  return bodyContent.includes(signature.content) || bodyContent.includes(signatureText);
}

/**
 * Check if body contains signature marker
 */
export function hasSignatureMarker(bodyContent: string): boolean {
  return bodyContent.includes('data-signature="true"');
}

/**
 * Extract body without signature
 */
export function getBodyWithoutSignature(bodyContent: string): string {
  // Remove signature block if present
  const signatureRegex = /<div[^>]*data-signature="true"[^>]*>[\s\S]*?<\/div>/gi;
  return bodyContent.replace(signatureRegex, "").trim();
}
